import React from "react";
import { CardBody, Col, Input, Label, Row } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import TextareaAutosize from "react-textarea-autosize";
import styles from "../../styles.scss";
import classNames from "classnames";
import { FormGroup, FormControlLabel, Switch } from "@mui/material";
import TooltipColorPicker from "../../common/tooltipColorPicker";
import MessageButtonControls from "../../common/messageButtonControls";
import SectionLabel from "../../common/sectionLabel";
import { LeftSidebarVariablesProp } from "../../common/types/variables";
import ImageSelectionInput from "../../common/imageSelectInput";
import { CharityAssetsService } from "../../../../../../../../../services/charityAssetsService";

const MAIN_MESSAGE_DESKTOP_MAX_LENGTH = 70;
const SECONDARY_MESSAGE_DESKTOP_MAX_LENGTH = 250;

const GivingBannerSection = ({ variables, charityId, firebase, onError }: LeftSidebarVariablesProp) => {
  const [overlay, setOverlay] = variables["includeOverlay"];
  const [overlayColor, setOverlayColor] = variables["overlayColor"];

  const [bannerImageDesktop, setBannerImageDesktop] = variables["bannerImageDesktop"];

  const [bannerImageMobile, setBannerImageMobile] = variables["bannerImageMobile"];

  const [mainMessageDesktop, setMainMessageDesktop] = variables[
    "mainMessageDesktop"
  ];

  const [mainMessageDesktopColor, setMainMessageDesktopColor] = variables[
    "mainMessageDesktopColor"
  ];

  const [mainMessageMobile, setMainMessageMobile] = variables[
    "mainMessageMobile"
  ];

  const [mainMessageMobileColor, setMainMessageMobileColor] = variables[
    "mainMessageMobileColor"
  ];

  const [mainMessageActiveButtons, setMainMessageActiveButtons] = variables[
    "mainMessageActiveButtons"
  ];

  const handleMainTextFormatClick = format => {
    setMainMessageActiveButtons(prev => ({
      ...prev,
      textFormat: format,
    }));
  };

  const handleMainTextAlignmentClick = alignment => {
    setMainMessageActiveButtons(prev => ({
      ...prev,
      textAlignment: alignment,
    }));
  };

  const [secondaryMessageDesktop, setSecondaryMessageDesktop] = variables[
    "secondaryMessageDesktop"
  ];

  const [secondaryMessageMobile, setSecondaryMessageMobile] = variables[
    "secondaryMessageMobile"
  ];

  const [
    secondaryMessageDesktopColor,
    setSecondaryMessageDesktopColor,
  ] = variables["secondaryMessageDesktopColor"];
  const [
    secondaryMessageMobileColor,
    setSecondaryMessageMobileColor,
  ] = variables["secondaryMessageMobileColor"];

  const [
    secondaryMessageActiveButtons,
    setSecondaryMessageActiveButtons,
  ] = variables["secondaryMessageActiveButtons"];

  const [isMobileView, _] = variables["isMobileView"];

  const handleSecondaryTextAlignmentClick = alignment => {
    setSecondaryMessageActiveButtons(prev => ({
      ...prev,
      textAlignment: alignment,
    }));
  };

  const handleBannerUpload = async (file: File): Promise<string> => {
    if (!charityId) {
      throw new Error('Charity ID is required for banner upload');
    }
    if (!firebase) {
      throw new Error('Firebase instance is required for banner upload');
    }
    const assetsService = new CharityAssetsService(firebase);
    const bannerName = isMobileView ? 'mobile' : 'desktop';
    return await assetsService.uploadBanner(file, charityId, bannerName);
  };

  return (
    <CardBody>
      {/* Banner Image Section */}
      <ImageSelectionInput
        label={
          <SectionLabel
            label={`${isMobileView ? "Mobile" : "Desktop"} Image`}
            tooltip="Change the image of your banner to make sure you're driving attention towards your goals"
            subtitle="Min. size 900×350 px. PNG format."
          />
        }
        image={isMobileView ? bannerImageMobile : bannerImageDesktop}
        setImage={isMobileView ? setBannerImageMobile : setBannerImageDesktop}
        onUpload={handleBannerUpload}
        defaultImage="https://placehold.co/900x350/000000/FFFFFF/png"
        altText="Banner Image"
        onError={onError}
      />
      <div className={styles.hiddenSection}>
        <FormGroup>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "16px",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={overlay}
                  onChange={e => setOverlay(!overlay)}
                />
              }
              label={<Label className={styles.switchLabel}>Add Overlay</Label>}
            />
            {overlay && (
              <TooltipColorPicker
                value={overlayColor}
                setValue={setOverlayColor}
                maxInputWidth="105px"
                includeAlphaValue
              />
            )}
          </div>
        </FormGroup>
      </div>

      {/* Main Message Section */}
      <div className={styles.section}>
        <SectionLabel
          label={`Main message${isMobileView ? " (Mobile)" : " (Desktop)"}`}
          children={
            !isMobileView && (
              <MessageButtonControls
                activeButtons={mainMessageActiveButtons}
                handleTextAlignmentClick={handleMainTextAlignmentClick}
                handleTextFormatClick={handleMainTextFormatClick}
                showFormatControls
              />
            )
          }
        />
        {isMobileView ? (
          <TextareaAutosize
            className={classNames(styles.textarea, styles.mainMessage)}
            placeholder="Enter the main message for the banner in the mobile view"
            value={mainMessageMobile}
            onChange={e => setMainMessageMobile(e.target.value)}
            minRows={2}
            maxRows={6}
          />
        ) : (
          <TextareaAutosize
            className={classNames(styles.textarea, styles.mainMessage)}
            placeholder="Enter the main message for the banner in the desktop view"
            value={mainMessageDesktop}
            maxLength={MAIN_MESSAGE_DESKTOP_MAX_LENGTH}
            onChange={e =>
              setMainMessageDesktop(
                e.target.value.slice(0, MAIN_MESSAGE_DESKTOP_MAX_LENGTH)
              )
            }
            minRows={2}
            maxRows={6}
          />
        )}

        <Row>
          {isMobileView ? (
            <Col>
              <SectionLabel
                label="Mobile Color"
                tooltip="Personalize the color of the main message when being seen from a mobile device"
              />
              <TooltipColorPicker
                value={mainMessageMobileColor}
                setValue={setMainMessageMobileColor}
              />
            </Col>
          ) : (
            <Col>
              <SectionLabel
                label="Desktop Color"
                tooltip="Personalize the color of the main message when being seen from a desktop device"
              />
              <TooltipColorPicker
                value={mainMessageDesktopColor}
                setValue={setMainMessageDesktopColor}
              />
            </Col>
          )}
        </Row>
      </div>

      {/* Secondary Message Section */}
      <div className={styles.section}>
        <SectionLabel
          label={`Secondary message${
            isMobileView ? " (Mobile)" : " (Desktop)"
          }`}
          children={
            !isMobileView && (
              <MessageButtonControls
                activeButtons={secondaryMessageActiveButtons}
                handleTextAlignmentClick={handleSecondaryTextAlignmentClick}
              />
            )
          }
        />
        {isMobileView ? (
          <TextareaAutosize
            className={classNames(styles.textarea, styles.mainMessage)}
            placeholder="This is the Secondary message for a mobile device (i.e. a phone or a tablet). This is a short message that invites your donor to make a donation. We recommend explaining your vision or your mission, and the scope of your organization."
            value={secondaryMessageMobile}
            onChange={e => setSecondaryMessageMobile(e.target.value)}
            minRows={3}
            maxRows={10}
          />
        ) : (
          <TextareaAutosize
            className={classNames(styles.textarea, styles.mainMessage)}
            placeholder="This is the Secondary message for a desktop device (i.e. a computer). This is a short message that invites your donor to make a donation. We recommend explaining your vision or your mission, and the scope of your organization."
            value={secondaryMessageDesktop}
            maxLength={SECONDARY_MESSAGE_DESKTOP_MAX_LENGTH}
            onChange={e =>
              setSecondaryMessageDesktop(
                e.target.value.slice(0, SECONDARY_MESSAGE_DESKTOP_MAX_LENGTH)
              )
            }
            minRows={3}
            maxRows={10}
          />
        )}
        <Row>
          {isMobileView ? (
            <Col>
              <SectionLabel
                label="Mobile Color"
                tooltip="Personalize the color of the secondary message when being seen from a mobile device"
              />
              <TooltipColorPicker
                value={secondaryMessageMobileColor}
                setValue={setSecondaryMessageMobileColor}
              />
            </Col>
          ) : (
            <Col>
              <SectionLabel
                label="Desktop Color"
                tooltip="Personalize the color of the secondary message when being seen from a desktop device"
              />
              <TooltipColorPicker
                value={secondaryMessageDesktopColor}
                setValue={setSecondaryMessageDesktopColor}
              />
            </Col>
          )}
        </Row>
      </div>
    </CardBody>
  );
};

export default withStyles(styles)(GivingBannerSection);
